import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Button, Typography, Tag, Divider } from 'antd';
import {
  LogoutOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import useMobileMenuItems from './useMobileMenuItems';
import TaskBell from '../components/TaskBell';

// Полноэкранная /menu страница для мобилки.
//
// Раскладка сверху вниз:
// 1. User-card (аватар + имя/роль + impersonation-тег + TaskBell) — не скроллится.
// 2. Скроллируемый блок с пунктами меню.
//    - Когда пунктов мало → margin-top: auto прижимает их к низу блока
//      (thumb-zone: верхние ~25-30vh пусты, пальцу не до них).
//    - Когда пунктов много → блок скроллится, стартовая позиция — низ
//      (нижние пункты в thumb zone, scroll вверх показывает верхние).
// 3. Footer: «Выйти» + «Закрыть» — не скроллятся.

const MobileMenuPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, stopImpersonation } = useAuthStore();
  const items = useMobileMenuItems();
  const scrollRef = useRef<HTMLDivElement>(null);

  // На mount прокручиваем скролл-контейнер в конец, чтобы последние
  // пункты меню оказались в thumb-zone. Если пунктов мало и переполнения
  // нет — scrollTo не делает ничего.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // requestAnimationFrame даёт AntD Menu отрендерить submenu до того,
    // как мы читаем scrollHeight.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        zIndex: 1100,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* User card — sticky top, не скроллится */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: '#e6f4ff',
              color: '#1677ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            <UserOutlined />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Text strong style={{ fontSize: 16, display: 'block' }} ellipsis>
              {user?.display_name || user?.username}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Роль: {user?.role}
            </Typography.Text>
            {user?.impersonating && (
              <div style={{ marginTop: 4 }}>
                <Tag
                  color="warning"
                  closable
                  closeIcon={<CloseCircleOutlined />}
                  onClose={() => {
                    stopImpersonation();
                    navigate('/');
                  }}
                >
                  admin &rarr; {user.role}
                </Tag>
              </div>
            )}
          </div>
        </div>
        <TaskBell />
      </div>

      {/* Скроллируемые пункты меню. */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',  // плавный momentum-скролл iOS
        }}
      >
        {/* Распорка с margin-top: auto тянет меню к низу когда есть свободное место */}
        <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: '1 1 auto' }} />
          <Menu
            mode="inline"
            items={items}
            selectedKeys={[]}
            style={{ borderInlineEnd: 'none' }}
            onClick={({ key }) => {
              if (!key.startsWith('/')) return;
              navigate(key);
            }}
          />
          <Divider style={{ margin: '8px 0 0' }} />
          <Button
            type="text"
            danger
            block
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              textAlign: 'left',
              padding: '12px 16px',
              height: 'auto',
              fontSize: 16,
            }}
          >
            Выйти
          </Button>
        </div>
      </div>

      {/* Нижняя кнопка «Закрыть» — всегда видна */}
      <Button
        type="default"
        icon={<CloseOutlined />}
        block
        onClick={goBack}
        style={{
          height: `calc(48px + env(safe-area-inset-bottom, 0px))`,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          borderRadius: 0,
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        Закрыть
      </Button>
    </div>
  );
};

export default MobileMenuPage;
